import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { leiderschapApi } from '../api';
import type { LeadershipDocument } from '../types';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsTab({ meetingId, canManage }: { meetingId: string; canManage: boolean }) {
  const [documents, setDocuments] = useState<LeadershipDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = async () => {
    try {
      const data = await leiderschapApi.listDocuments(meetingId);
      setDocuments(data);
    } catch (error) {
      toast.error('Kon documenten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await leiderschapApi.uploadDocument(meetingId, file);
      toast.success('Document geüpload');
      fetchDocuments();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Uploaden mislukt');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: LeadershipDocument) => {
    try {
      await leiderschapApi.downloadDocument(meetingId, doc.id, doc.original_filename);
    } catch (error) {
      toast.error('Downloaden mislukt');
    }
  };

  const handleDelete = async (doc: LeadershipDocument) => {
    if (!window.confirm(`"${doc.original_filename}" verwijderen?`)) return;
    try {
      await leiderschapApi.deleteDocument(meetingId, doc.id);
      fetchDocuments();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  return (
    <div className="space-y-3">
      {!loading && documents.length === 0 && <p className="text-sm text-slate-500">Nog geen documenten.</p>}
      {documents.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3">
          <div className="flex min-w-0 items-center gap-3">
            <FileText className="h-5 w-5 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{doc.original_filename}</p>
              <p className="text-xs text-slate-500">{formatSize(doc.size_bytes)}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)} aria-label="Downloaden">
              <Download className="h-4 w-4 text-slate-500" />
            </Button>
            {canManage && (
              <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)} aria-label="Verwijderen">
                <Trash2 className="h-4 w-4 text-slate-400" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {canManage && (
        <div className="pt-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleUpload}
            className="hidden"
            id="leiderschap-document-upload"
          />
          <Button variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? 'Uploaden...' : 'Document uploaden'}
          </Button>
        </div>
      )}
    </div>
  );
}
